import React, { useState, useEffect } from 'react';
import { useLocation, Link as ReactRouterLink } from 'react-router-dom';
import { Box, Heading, Text, List, ListItem, Spinner, useColorModeValue, FormControl, FormLabel, Input, Button, Textarea, HStack, VStack, Select, Checkbox, IconButton } from '@chakra-ui/react';
import getCSRFtoken from '../utils/getCSRFtoken';
import { useToast } from '../hooks/useToast';
import Breadcrumbs from './shared/Breadcrumbs';
import CollapsibleSection from './shared/CollapsibleSection';
import TargetDemographicsForm from './TargetDemographicsForm';
import SampleSizeCalculator from './SampleSizeCalculator';
import fetchWithAuth from '../utils/fetchWithAuth';

const SAMPLE_LINK = 'https://bbr.la/r/a1b2c3d4';
const LINK_OVERHEAD = 2 + SAMPLE_LINK.length; // "\n\n" + link

function SmsPreview({ text, label }) {
    const preview = (text || '').replace('{first_name}', 'Jane');
    const fullMessage = preview + '\n\n' + SAMPLE_LINK;
    const charCount = fullMessage.length;
    // GSM-7: 160 chars for 1 segment, 153 per segment after that
    // Unicode: 70 chars for 1 segment, 67 per segment after that
    const isUnicode = /[^\x00-\x7F]/.test(fullMessage);
    const singleLimit = isUnicode ? 70 : 160;
    const multiLimit = isUnicode ? 67 : 153;
    const segments = charCount <= singleLimit ? 1 : Math.ceil(charCount / multiLimit);
    const costPerSegment = 0.0079;
    const segmentColor = segments > 2 ? 'orange.600' : segments > 1 ? 'yellow.700' : 'green.600';

    if (!text) return null;

    return (
        <Box mt={2} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" fontFamily="mono">
            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
                {label || 'SMS PREVIEW'} — what the recipient will receive:
            </Text>
            <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.800">
                {preview}
            </Text>
            <Text fontSize="sm" color="blue.500" mt={1}>
                {SAMPLE_LINK}
            </Text>
            <HStack mt={2} spacing={4} fontSize="xs">
                <Text color={segmentColor} fontWeight="600">
                    {charCount} chars / {segments} segment{segments !== 1 ? 's' : ''}
                </Text>
                <Text color="gray.500">
                    ~${(segments * costPerSegment).toFixed(3)}/msg
                </Text>
                {isUnicode && <Text color="orange.500">Unicode detected</Text>}
            </HStack>
        </Box>
    );
}

function StagingPoll() {
    const location = useLocation();
    const [polls, setPolls] = useState([]);
    const [audiences, setAudiences] = useState([]);
    const [selectedPoll, setSelectedPoll] = useState('');
    const [selectedAudience, setSelectedAudience] = useState('');
    const [invitationText, setInvitationText] = useState('');
    const [invitationTextEs, setInvitationTextEs] = useState('');
    const [prologue, setPrologue] = useState('Thank you for participating in this survey about issues in your community. Your responses are anonymous and will help inform public policy discussions.');
    const [prologueEs, setPrologueEs] = useState('Gracias por participar en esta encuesta sobre temas de su comunidad. Sus respuestas son anonimas y ayudaran a informar las discusiones de politicas publicas.');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [stagedPolls, setStagedPolls] = useState([]);
    const [selectedStagedPoll, setSelectedStagedPoll] = useState(null);
    const [respondents, setRespondents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showStageForm, setShowStageForm] = useState(false);
    const [targetDemographicsConfig, setTargetDemographicsConfig] = useState(null);
    const [selectedAudiencePopulation, setSelectedAudiencePopulation] = useState(null);
    const [audienceSize, setAudienceSize] = useState(0);
    const [abEnabled, setAbEnabled] = useState(false);
    const [abVariants, setAbVariants] = useState([
        { label: 'A', text: '', text_es: '' },
        { label: 'B', text: '', text_es: '' },
    ]);
    const csrfToken = getCSRFtoken();
    const toast = useToast();

    const bgColor = useColorModeValue('background.shade1');
    const color = useColorModeValue('theme.black');
    const pollBoxBgColor = useColorModeValue('background.shade2');

    useEffect(() => {
        fetchData();
    }, []);

    // Auto-select poll if navigated from poll management page
    useEffect(() => {
        if (location.state?.selectedPollId && polls.length > 0) {
            setSelectedPoll(location.state.selectedPollId);
            setShowStageForm(true);
        }
    }, [location.state, polls]);


    const fetchData = async () => {
        try {
            setLoading(true);
            const [pollsResponse, audiencesResponse, stagedPollsResponse] = await Promise.all([
                fetchWithAuth("/api/polls?state=draft"),
                fetchWithAuth("/api/audiences/"),
                fetchWithAuth("/api/polls?state=staged")
            ]);

            if (!pollsResponse.ok || !audiencesResponse.ok || !stagedPollsResponse.ok) {
                throw new Error('Failed to fetch data');
            }

            const [pollsData, audiencesData, stagedPollsData] = await Promise.all([
                pollsResponse.json(),
                audiencesResponse.json(),
                stagedPollsResponse.json()
            ]);

            setPolls(pollsData.results || []);
            setAudiences(audiencesData.results || []);
            setStagedPolls(stagedPollsData.results || []);
            setError(null);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAudienceChange = async (audienceId) => {
        setSelectedAudience(audienceId);
        // Find the selected audience to get its population
        const audience = audiences.find(a => a.id === parseInt(audienceId));
        if (audience) {
            setSelectedAudiencePopulation(audience.population);

            // Fetch detailed audience info to get subframe_size
            try {
                const response = await fetchWithAuth(`/api/audiences/${audienceId}/`);
                if (response.ok) {
                    const audienceDetails = await response.json();
                    setAudienceSize(audienceDetails.subframe_size || 0);
                }
            } catch (error) {
                console.error('Error fetching audience details:', error);
            }
        }
    };

    const handleStagePoll = async () => {
        if (
            !selectedPoll ||
            !selectedAudience ||
            !invitationText ||
            !prologue ||
            !startDate ||
            !endDate
        ) {
            toast.showError('Validation Error', 'Please fill in all fields');
            return;
        }

        const postData = {
            audienceId: selectedAudience,
            invitationText,
            invitationTextEs: invitationTextEs || undefined,
            prologue,
            prologueEs: prologueEs || undefined,
            startDate,
            endDate,
            ...(targetDemographicsConfig || {}),
            invitation_variants: abEnabled ? abVariants.filter(v => v.text.trim()) : null,
        };
    
        try {
            const response = await fetchWithAuth(`/api/polls/${selectedPoll}/stage/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify(postData),
            });
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to stage poll');
            }
    
            await response.json();
            fetchData();
            setShowStageForm(false);
            // Reset form
            setSelectedPoll('');
            setSelectedAudience('');
            setInvitationText('');
            setPrologue('');
            setStartDate('');
            setEndDate('');
            toast.showSuccess('Success', 'Poll staged successfully');
        } catch (error) {
            console.error('Error staging poll:', error);
            toast.showError('Error', `Failed to stage poll: ${error.message}`);
        }
    };    

    const handleStagedPollClick = async (poll, view) => {
        setSelectedStagedPoll(prevPoll => prevPoll?.id === poll.id ? null : poll);
        setSelectedView(view);

        if (view === 'respondents' && poll.id !== selectedStagedPoll?.id) {
            try {
                const response = await fetchWithAuth(`/api/polls/${poll.id}/respondents/`);
                const data = await response.json();
                setRespondents(data.respondents || []);
            } catch (error) {
                console.error("Error fetching respondents:", error);
            }
        }
    };

    const [selectedView, setSelectedView] = useState(null);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="xl" />
            </Box>
        );
    }

    if (error) {
        return (
            <Box textAlign="center" color="red.500" mt={8}>
                <Text>Error: {error}</Text>
            </Box>
        );
    }

    return (
        <Box bg={bgColor} color={color} minHeight="100vh">
            <Box maxWidth="800px" margin="0 auto" px={4} py={8}>
                <Breadcrumbs
                    items={[
                        { label: 'Home', path: '/' },
                        { label: 'Polls', path: '/pollsPage' },
                        { label: 'Stage Poll' }
                    ]}
                />
                <Heading as="h1" size="xl" mb={6}>
                    Poll Staging
                </Heading>

                <CollapsibleSection
                    title="Stage a New Poll"
                    isOpen={showStageForm}
                    onToggle={() => setShowStageForm(!showStageForm)}
                >
                        <FormControl id="selectPoll" mb={4}>
                            <FormLabel>Select a Draft Poll</FormLabel>
                            <Select value={selectedPoll} onChange={e => setSelectedPoll(e.target.value)} placeholder="Select a poll">
                                {polls.map(poll => (
                                    <option key={poll.id} value={poll.id}>
                                        {poll.title}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl id="selectAudience" mb={4}>
                            <FormLabel>Select an Audience</FormLabel>
                            <Select value={selectedAudience} onChange={e => handleAudienceChange(e.target.value)} placeholder="Select an audience">
                                {audiences.map(audience => (
                                    <option key={audience.id} value={audience.id}>
                                        {audience.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Sample Size Calculator */}
                        {selectedAudience && audienceSize > 0 && (
                            <SampleSizeCalculator
                                audienceSize={audienceSize}
                                onSampleSizeChange={(sampleSize) => {
                                    // Update the target demographics config with the new sample size
                                    setTargetDemographicsConfig(prev => ({
                                        ...prev,
                                        response_goal: sampleSize
                                    }));
                                }}
                            />
                        )}

                        {/* Target Demographics Configuration */}
                        {selectedAudiencePopulation && (
                            <Box mb={4}>
                                <TargetDemographicsForm
                                    populationId={selectedAudiencePopulation}
                                    audienceId={selectedAudience}
                                    onChange={setTargetDemographicsConfig}
                                />
                            </Box>
                        )}

                        <FormControl id="invitationText" mb={4}>
                            <FormLabel>Invitation Text (English)</FormLabel>
                            <Textarea value={invitationText} onChange={e => setInvitationText(e.target.value)} />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Use <strong>{'{first_name}'}</strong> to personalize with the recipient's name.
                            </Text>
                            <SmsPreview text={invitationText} label="ENGLISH SMS PREVIEW" />
                        </FormControl>
                        <FormControl id="invitationTextEs" mb={4}>
                            <FormLabel color="gray.500">Invitation Text (Spanish)</FormLabel>
                            <Textarea value={invitationTextEs} onChange={e => setInvitationTextEs(e.target.value)} placeholder="Texto de invitacion en espanol (opcional)" />
                            <SmsPreview text={invitationTextEs} label="SPANISH SMS PREVIEW" />
                        </FormControl>

                        {/* A/B Testing Variants */}
                        <Box mb={4} p={4} borderRadius="md" border="1px solid" borderColor="gray.200">
                            <Checkbox
                                isChecked={abEnabled}
                                onChange={e => setAbEnabled(e.target.checked)}
                                mb={abEnabled ? 3 : 0}
                            >
                                <Text fontSize="sm" fontWeight="600">A/B test invitation text</Text>
                            </Checkbox>
                            {abEnabled && (
                                <VStack spacing={4} align="stretch">
                                    <Text fontSize="xs" color="gray.500">
                                        Each variant will be sent to an equal random split of recipients. Use {'{first_name}'} for personalization.
                                    </Text>
                                    {abVariants.map((variant, idx) => (
                                        <Box key={variant.label} p={3} bg="gray.50" borderRadius="md">
                                            <HStack justify="space-between" mb={2}>
                                                <Text fontSize="sm" fontWeight="bold">Variant {variant.label}</Text>
                                                {abVariants.length > 2 && (
                                                    <Button
                                                        size="xs"
                                                        variant="ghost"
                                                        colorScheme="red"
                                                        onClick={() => setAbVariants(abVariants.filter((_, i) => i !== idx))}
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </HStack>
                                            <Textarea
                                                size="sm"
                                                value={variant.text}
                                                onChange={e => {
                                                    const updated = [...abVariants];
                                                    updated[idx] = { ...updated[idx], text: e.target.value };
                                                    setAbVariants(updated);
                                                }}
                                                placeholder={`English text for variant ${variant.label}...`}
                                                mb={2}
                                            />
                                            <Textarea
                                                size="sm"
                                                value={variant.text_es}
                                                onChange={e => {
                                                    const updated = [...abVariants];
                                                    updated[idx] = { ...updated[idx], text_es: e.target.value };
                                                    setAbVariants(updated);
                                                }}
                                                placeholder={`Spanish text for variant ${variant.label} (optional)...`}
                                            />
                                            <SmsPreview text={variant.text} label={`VARIANT ${variant.label} PREVIEW`} />
                                        </Box>
                                    ))}
                                    {abVariants.length < 4 && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                const nextLabel = String.fromCharCode(65 + abVariants.length);
                                                setAbVariants([...abVariants, { label: nextLabel, text: '', text_es: '' }]);
                                            }}
                                        >
                                            Add Variant {String.fromCharCode(65 + abVariants.length)}
                                        </Button>
                                    )}
                                </VStack>
                            )}
                        </Box>
                        <FormControl id="prologue" mb={4}>
                            <FormLabel>Prologue (English)</FormLabel>
                            <Textarea value={prologue} onChange={e => setPrologue(e.target.value)} />
                        </FormControl>
                        <FormControl id="prologueEs" mb={4}>
                            <FormLabel color="gray.500">Prologue (Spanish)</FormLabel>
                            <Textarea value={prologueEs} onChange={e => setPrologueEs(e.target.value)} placeholder="Prologo en espanol (opcional)" />
                        </FormControl>
                        <FormControl id="startDate" mb={4}>
                            <FormLabel>Start Date</FormLabel>
                            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </FormControl>
                        <FormControl id="endDate" mb={4}>
                            <FormLabel>End Date</FormLabel>
                            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </FormControl>
                        <Button onClick={handleStagePoll} variant="primary" size="lg" width="full" mt={4}>
                            Stage Poll
                        </Button>
                </CollapsibleSection>

                <Box mt={8}>
                    <HStack justify="space-between" mb={4}>
                        <Heading as="h2" size="lg">
                            Staged Polls
                        </Heading>
                        {stagedPolls.length > 0 && (
                            <Text fontSize="sm" color="gray.500">
                                {stagedPolls.length} poll{stagedPolls.length !== 1 ? 's' : ''} ready
                            </Text>
                        )}
                    </HStack>

                    {stagedPolls.length === 0 ? (
                        <Box
                            bg={pollBoxBgColor}
                            p={8}
                            borderRadius="md"
                            border="1px solid"
                            borderColor="gray.300"
                            textAlign="center"
                        >
                            <Text color="gray.500">No staged polls. Stage a poll above to get started.</Text>
                        </Box>
                    ) : (
                        <List spacing={3}>
                            {stagedPolls.map(poll => (
                                <Box key={poll.id}>
                                    <ListItem
                                        bg={pollBoxBgColor}
                                        p={4}
                                        borderRadius="md"
                                        border="1px solid"
                                        borderColor="gray.300"
                                        _hover={{ borderColor: "gray.400", shadow: "sm" }}
                                    >
                                        <HStack justify="space-between" align="start" mb={3}>
                                            <Heading as="h3" size="md" flex="1">
                                                {poll.title}
                                            </Heading>
                                        </HStack>
                                        <HStack spacing={2} flexWrap="wrap">
                                            <Button
                                                as={ReactRouterLink}
                                                to={`/polls/${poll.id}/settings`}
                                                colorScheme="green"
                                                size="sm"
                                            >
                                                Set Live
                                            </Button>
                                            <Button
                                                as={ReactRouterLink}
                                                to={`/manage-questions/${poll.id}`}
                                                variant="outline"
                                                size="sm"
                                            >
                                                Manage
                                            </Button>
                                            <Button
                                                onClick={() => handleStagedPollClick(poll, 'respondents')}
                                                variant="ghost"
                                                size="sm"
                                            >
                                                Respondents
                                            </Button>
                                            <Button
                                                onClick={() => handleStagedPollClick(poll, 'textOutput')}
                                                variant="ghost"
                                                size="sm"
                                            >
                                                Preview
                                            </Button>
                                        </HStack>
                                    </ListItem>
                                    {selectedStagedPoll?.id === poll.id && (
                                        <Box mt={3} ml={4}>
                                            {selectedView === 'respondents' && (
                                                <Box bg={pollBoxBgColor} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
                                                        <Heading as="h3" textStyle="h3" size="lg" mb={4}>
                                                            Respondents for {selectedStagedPoll.title}
                                                        </Heading>
                                                        <List spacing={4}>
                                                            {respondents.map(respondent => (
                                                                <ListItem key={respondent.id} bg={pollBoxBgColor} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
                                                                    <Text textStyle="p" mb={2}>
                                                                        {respondent.phone_number}
                                                                    </Text>
                                                                    <a href={respondent.unique_link}>Survey Link</a>
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </Box>
                                                )}
                                                {selectedView === 'textOutput' && (
                                                    <Box bg={pollBoxBgColor} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
                                                        <Heading as="h3" textStyle="h3" size="lg" mb={4}>
                                                            Text Output for {selectedStagedPoll.title}
                                                        </Heading>
                                                        <Textarea
                                                            value={respondents.map(respondent =>
                                                                `${respondent.phone_number} ${selectedStagedPoll.text_invitation} ${respondent.unique_link}`
                                                            ).join('\n')}
                                                            readOnly
                                                            height="200px"
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </List>
                        )}
                </Box>
            </Box>
        </Box>
    );
}

export default StagingPoll;
